import { populationsApi } from '@/lib/api'
import type { AudienceSummary, FeaturedTwin, FeaturedTwinListItem, TargetingPreferences } from '@/lib/api'
import { rankAudiencesByRelevance } from '@/lib/audience-relevance'

const AUDIENCE_PAGE_SIZE = 200
export const HOME_STUDY_AUDIENCE_LIMIT = 3
export const HOME_FEATURED_TWIN_LIMIT = 7

export async function fetchAllPopulations(): Promise<Array<AudienceSummary>> {
  const items: Array<AudienceSummary> = []
  let page = 1
  let total = Number.POSITIVE_INFINITY

  while (items.length < total) {
    const result = await populationsApi.listPaginated({
      page,
      limit: AUDIENCE_PAGE_SIZE,
    })
    items.push(...result.items)
    total = result.total
    if (result.items.length === 0) {
      break
    }
    page += 1
  }

  return items
}

export async function fetchAllReadyAudiences(): Promise<Array<AudienceSummary>> {
  const items = await fetchAllPopulations()
  return items.filter((audience) => audience.status === 'ready')
}

export function selectBookmarkedAudiencesForHomepage(
  audiences: Array<AudienceSummary>,
  targeting: TargetingPreferences | null | undefined,
  limit = HOME_STUDY_AUDIENCE_LIMIT,
): Array<AudienceSummary> {
  const bookmarked = audiences.filter(
    (audience) => audience.status === 'ready' && audience.isBookmarked === true,
  )
  if (bookmarked.length === 0) {
    return []
  }
  const ranked = rankAudiencesByRelevance(bookmarked, targeting)

  return ranked
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score
      }
      const leftBookmarkedAt = getTimestamp(left.audience.bookmarkedAt)
      const rightBookmarkedAt = getTimestamp(right.audience.bookmarkedAt)
      if (rightBookmarkedAt !== leftBookmarkedAt) {
        return rightBookmarkedAt - leftBookmarkedAt
      }
      return left.originalIndex - right.originalIndex
    })
    .slice(0, limit)
    .map((entry) => entry.audience)
}

/**
 * Selects the initial featured-twin seed from onboarding-bookmarked audiences.
 * Seeded twins are marked pinned; users can depin or remove them after onboarding.
 */
export function selectFeaturedTwinsFromBookmarkedAudiences({
  bookmarkedAudiences,
  marketplaceTwins,
  limit = HOME_FEATURED_TWIN_LIMIT,
}: {
  bookmarkedAudiences: Array<AudienceSummary>
  marketplaceTwins: Array<FeaturedTwin>
  limit?: number
}): Array<FeaturedTwin> {
  const byAudience = new Map<string, Array<FeaturedTwin>>()
  for (const twin of marketplaceTwins) {
    if (!isDisplayableTwin(twin) || !twin.populationId) {
      continue
    }
    const group = byAudience.get(twin.populationId) ?? []
    group.push(twin)
    byAudience.set(twin.populationId, group)
  }

  const selected: Array<FeaturedTwin> = []
  const selectedPersonaIds = new Set<string>()
  const selectedImageKeys = new Set<string>()

  for (const audience of bookmarkedAudiences) {
    for (const twin of byAudience.get(audience.id) ?? []) {
      if (selected.length >= limit) {
        return selected
      }
      if (isDuplicateTwin(twin, selectedPersonaIds, selectedImageKeys)) {
        continue
      }
      markTwinSelected(twin, selectedPersonaIds, selectedImageKeys)
      selected.push({ ...twin, pinned: true })
    }
  }

  return selected
}

export async function fetchFeaturedTwinPoolForAudiences(
  audiences: Array<AudienceSummary>,
): Promise<Array<FeaturedTwin>> {
  const [globalTwinsResult, audienceTwinsResult] = await Promise.allSettled([
    populationsApi.getAllFeaturedTwins() as Promise<Array<FeaturedTwin>>,
    fetchGeneratedFeaturedTwinsForAudiences(audiences),
  ])

  return [
    ...(globalTwinsResult.status === 'fulfilled' ? globalTwinsResult.value : []),
    ...(audienceTwinsResult.status === 'fulfilled' ? audienceTwinsResult.value : []),
  ]
}

export function toFeaturedTwinUpdatePayload(twin: FeaturedTwin): FeaturedTwin {
  return {
    personaId: twin.personaId,
    personaName: twin.personaName,
    imageId: twin.imageId,
    imageUrl: twin.imageUrl,
    personaAttributes: twin.personaAttributes,
    compressedAttributes: twin.compressedAttributes,
    personaSummary: twin.personaSummary,
    personaPrompt: twin.personaPrompt,
    enrichedProfile: twin.enrichedProfile,
    voiceProfile: twin.voiceProfile,
    populationId: twin.populationId,
    populationName: twin.populationName,
    columnCategories: twin.columnCategories,
    aiTags: twin.aiTags,
    personaTagline: twin.personaTagline,
    pinned: twin.pinned,
  }
}

function getTimestamp(value: string | null | undefined): number {
  if (!value) {
    return 0
  }
  const timestamp = new Date(value).getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}

async function fetchGeneratedFeaturedTwinsForAudiences(
  audiences: Array<AudienceSummary>,
): Promise<Array<FeaturedTwin>> {
  const results = await Promise.allSettled(
    audiences.map(async (audience) => {
      const twins = await populationsApi.getFeaturedTwins(audience.id)
      return twins.map((twin) => toSeedFeaturedTwin(twin, audience))
    }),
  )

  return results.flatMap((result) =>
    result.status === 'fulfilled' ? result.value : [],
  )
}

function toSeedFeaturedTwin(
  twin: FeaturedTwinListItem,
  audience: AudienceSummary,
): FeaturedTwin {
  return {
    personaId: twin.personaId,
    personaName: twin.personaName,
    imageId: twin.imageId ?? '',
    imageUrl: twin.imageUrl ?? '',
    demographics: twin.demographics,
    personaSummary: twin.personaSummary,
    personaTagline: twin.personaTagline ?? undefined,
    populationId: audience.id,
    populationName: audience.name,
  }
}

function isDisplayableTwin(twin: FeaturedTwin): boolean {
  return isPresentValue(twin.personaId) && isPresentValue(twin.personaName)
}

function isDuplicateTwin(
  twin: FeaturedTwin,
  personaIds: Set<string>,
  imageKeys: Set<string>,
): boolean {
  if (twin.personaId && personaIds.has(twin.personaId)) {
    return true
  }
  return getTwinImageKeys(twin).some((key) => imageKeys.has(key))
}

function markTwinSelected(
  twin: FeaturedTwin,
  personaIds: Set<string>,
  imageKeys: Set<string>,
): void {
  if (twin.personaId) {
    personaIds.add(twin.personaId)
  }
  getTwinImageKeys(twin).forEach((key) => imageKeys.add(key))
}

function getTwinImageKeys(twin: FeaturedTwin): Array<string> {
  const keys: Array<string> = []
  if (isPresentValue(twin.imageId)) {
    keys.push(`id:${twin.imageId.trim()}`)
  }
  if (isPresentValue(twin.imageUrl)) {
    keys.push(`url:${normalizeImageUrlForDedupe(twin.imageUrl)}`)
  }
  return keys
}

function normalizeImageUrlForDedupe(imageUrl: string): string {
  const trimmed = imageUrl.trim()
  try {
    const url = new URL(trimmed, 'http://local')
    return `${url.origin}${url.pathname}`
  } catch {
    return trimmed.split(/[?#]/)[0]
  }
}

function isPresentValue(value: string | null | undefined): value is string {
  const normalized = value?.trim()
  return Boolean(
    normalized &&
      normalized !== 'null' &&
      normalized !== 'undefined',
  )
}
